import { Avatar, Dropdown, Grid, Tooltip } from 'antd';
import {
  Menu as MenuIcon,
  Moon,
  Sun,
  LogOut,
  User2,
  Search,
  Settings,
  Monitor,
  AlignLeft,
  Rows3,
  Rows4,
  Circle,
  Keyboard,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useAuthStore } from '@features/auth/store';
import { authApi } from '@api/auth';
import { recordAuditAction } from '@features/audit/recordAction';
import { resolveIsDark, useUiStore, type Density, type Presence } from '@shared/stores/uiStore';
import { ROUTES } from '@shared/constants/routes';
import { useRoleLabel } from '@shared/constants/labels';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { NotificationBell } from '@features/notifications';
import { ProjectSwitcher } from '@features/projects/components/ProjectSwitcher';
import { useTranslation } from 'react-i18next';

const { useBreakpoint } = Grid;

function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/i.test(navigator.platform);
}

const DENSITY_ICON: Record<Density, typeof Rows3> = {
  compact: Rows3,
  default: AlignLeft,
  comfortable: Rows4,
};

export function Topbar() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleLabel = useRoleLabel();
  const { t } = useTranslation('app');
  const td = t as unknown as (key: string) => string;
  const densityLabel = (d: Density) => td(`topbar.density.${d}`);
  const presenceLabel = (p: Presence) => td(`topbar.presence.${p}`);
  const clearSession = useAuthStore((s) => s.clearSession);
  const setMobileDrawerOpen = useUiStore((s) => s.setMobileDrawerOpen);
  const colorScheme = useUiStore((s) => s.colorScheme);
  const setColorScheme = useUiStore((s) => s.setColorScheme);
  const density = useUiStore((s) => s.density);
  const setDensity = useUiStore((s) => s.setDensity);
  const setCommandPaletteOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const setShortcutSheetOpen = useUiStore((s) => s.setShortcutSheetOpen);
  const presence = useUiStore((s) => s.presence);
  const setPresence = useUiStore((s) => s.setPresence);

  const handleLogout = async () => {
    if (user) {
      await recordAuditAction({
        action: 'auth.logout',
        resource: 'auth',
        resourceId: user.id,
        summary: t('topbar.logoutSummary', { email: user.email }),
      });
    }
    try {
      // K-8: iptal edilecek refresh token httpOnly cookie'de — BE okur ve temizler.
      await authApi.logout();
    } catch {
      // logout fails are fine — clear session anyway
    }
    clearSession();
    void navigate(ROUTES.LOGIN, { replace: true });
  };

  const cmdKey = isMac() ? '⌘' : 'Ctrl';
  const isDark = resolveIsDark(colorScheme);

  const userMenu = {
    items: [
      {
        key: 'header',
        type: 'group' as const,
        label: (
          <div style={{ padding: '4px 0 8px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ds-text-primary)' }}>
              {user?.fullName ?? t('topbar.guest')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)' }}>{user?.email}</div>
          </div>
        ),
      },
      { type: 'divider' as const },
      {
        key: 'presence',
        icon: <Circle size={14} fill="currentColor" color={
          presence === 'online' ? 'var(--ds-success)' :
          presence === 'away' ? 'var(--ds-warning)' :
          presence === 'busy' ? 'var(--ds-danger)' :
          'var(--ds-text-tertiary)'
        } />,
        label: t('topbar.presenceStatus', { status: presenceLabel(presence) }),
        children: (['online', 'away', 'busy', 'offline'] as Presence[]).map((p) => ({
          key: `presence-${p}`,
          label: presenceLabel(p) + (presence === p ? '  ✓' : ''),
        })),
      },
      { key: 'profile', icon: <User2 size={14} />, label: t('topbar.menuItems.profile') },
      { key: 'settings', icon: <Settings size={14} />, label: t('topbar.menuItems.settings') },
      { key: 'shortcuts', icon: <Keyboard size={14} />, label: t('topbar.menuItems.shortcuts') },
      { type: 'divider' as const },
      {
        key: 'density',
        icon: <AlignLeft size={14} />,
        label: t('topbar.density.label'),
        children: (['compact', 'default', 'comfortable'] as Density[]).map((d) => ({
          key: `density-${d}`,
          label: densityLabel(d) + (density === d ? '  ✓' : ''),
        })),
      },
      {
        key: 'theme',
        icon: isDark ? <Sun size={14} /> : <Moon size={14} />,
        label: t('topbar.theme.label'),
        children: [
          {
            key: 'theme-light',
            icon: <Sun size={14} />,
            label: t('topbar.theme.light') + (colorScheme === 'light' ? '  ✓' : ''),
          },
          {
            key: 'theme-dark',
            icon: <Moon size={14} />,
            label: t('topbar.theme.dark') + (colorScheme === 'dark' ? '  ✓' : ''),
          },
          {
            key: 'theme-system',
            icon: <Monitor size={14} />,
            label: t('topbar.theme.system') + (colorScheme === 'system' ? '  ✓' : ''),
          },
        ],
      },
      { type: 'divider' as const },
      { key: 'logout', icon: <LogOut size={14} />, label: t('topbar.menuItems.logout'), danger: true },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'profile') void navigate(ROUTES.PROFILE);
      else if (key === 'settings') void navigate(ROUTES.SETTINGS);
      else if (key === 'shortcuts') setShortcutSheetOpen(true);
      else if (key === 'logout') void handleLogout();
      else if (key.startsWith('density-'))
        setDensity(key.replace('density-', '') as Density);
      else if (key.startsWith('presence-'))
        setPresence(key.replace('presence-', '') as Presence);
      else if (key === 'theme-light') setColorScheme('light');
      else if (key === 'theme-dark') setColorScheme('dark');
      else if (key === 'theme-system') setColorScheme('system');
    },
  };

  const initials = user?.fullName
    ?.split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const DensityIcon = DENSITY_ICON[density];

  return (
    <header className="ds-topbar" role="banner">
      <div className="ds-topbar__left">
        {isMobile ? (
          <button
            type="button"
            className="ds-icon-btn"
            onClick={() => setMobileDrawerOpen(true)}
            aria-label={t('topbar.menu')}
          >
            <MenuIcon size={18} strokeWidth={1.8} />
          </button>
        ) : null}

        <button
          type="button"
          className="ds-search-trigger"
          onClick={() => setCommandPaletteOpen(true)}
          aria-label={t('topbar.openCommandPalette')}
        >
          <Search size={14} strokeWidth={1.8} />
          <span className="ds-search-trigger__text">
            {isMobile ? t('topbar.searchPlaceholderShort') : t('topbar.searchPlaceholder')}
          </span>
          {!isMobile ? (
            <span className="ds-kbd" aria-hidden="true">
              {cmdKey} K
            </span>
          ) : null}
        </button>

        <ProjectSwitcher />
      </div>

      <div className="ds-topbar__right">
        <Tooltip
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {t('topbar.densityTooltip', { density: densityLabel(density) })}
            </span>
          }
        >
          <Dropdown
            menu={{
              items: (['compact', 'default', 'comfortable'] as Density[]).map((d) => ({
                key: d,
                label: densityLabel(d) + (density === d ? '  ✓' : ''),
              })),
              onClick: ({ key }) => setDensity(key as Density),
            }}
            placement="bottomRight"
          >
            <button type="button" className="ds-icon-btn" aria-label={t('topbar.density.label')}>
              <DensityIcon size={16} strokeWidth={1.8} />
            </button>
          </Dropdown>
        </Tooltip>

        <Tooltip
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {isDark ? t('topbar.theme.switchToLight') : t('topbar.theme.switchToDark')}
              <span
                style={{
                  fontSize: 10,
                  padding: '1px 5px',
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.18)',
                }}
              >
                ⇧{cmdKey}L
              </span>
            </span>
          }
        >
          <button
            type="button"
            className="ds-icon-btn"
            onClick={() => setColorScheme(isDark ? 'light' : 'dark')}
            aria-label={t('topbar.theme.toggle')}
          >
            {isDark ? <Sun size={16} strokeWidth={1.8} /> : <Moon size={16} strokeWidth={1.8} />}
          </button>
        </Tooltip>

        <NotificationBell />

        <div className="ds-topbar__divider" aria-hidden="true" />

        <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
          <button type="button" className="ds-user-trigger" aria-label={t('topbar.userMenu')}>
            <span className="ds-avatar-wrap">
              <Avatar
                size={32}
                src={resolveAssetUrl(user?.avatarUrl) ?? undefined}
                style={{
                  background: 'var(--ds-brand)',
                  color: 'var(--ds-text-on-brand)',
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                {initials ?? 'HD'}
              </Avatar>
              <span
                className={`ds-avatar-wrap__status ds-avatar-wrap__status--${presence}`}
                aria-label={presenceLabel(presence)}
              />
            </span>
            {!isMobile ? (
              <div className="ds-user-trigger__meta">
                <div className="ds-user-trigger__name">{user?.fullName ?? t('topbar.guest')}</div>
                <div className="ds-user-trigger__role">
                  {user ? roleLabel(user.role) : t('topbar.noSession')}
                </div>
              </div>
            ) : null}
          </button>
        </Dropdown>
      </div>
    </header>
  );
}
